import React, { useEffect, useState } from "react";
import { Card, Empty, StageChip, Stat } from "../../dashboard/primitives";
import { useAuth } from "../../auth/AuthContext";
import { subscribeMilestones, subscribeAccountProjects } from "../../projects/api";
import PayMilestoneModal from "../../components/PayMilestoneModal";
import { openCustomerPortal } from "../../lib/billing";
import { SkeletonList, SkeletonRow } from "../../components/Skeleton";
import { useMinLoading } from "../../lib/useMinLoading";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (ts) => {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};

const chipFor = (s) =>
  s === "paid" ? "paid" : s === "invoiced" ? "awaiting_approval" : "pending";

const ProjectInvoices = ({ project }) => {
  const [milestones, setMilestones] = useState(null);
  const [payingId, setPayingId] = useState(null);
  useEffect(() => subscribeMilestones(project.id, setMilestones), [project.id]);

  if (milestones === null) {
    return (
      <Card title={project.title}>
        <SkeletonList count={3} ariaLabel="Loading invoices">
          <SkeletonRow cols={5} widths={["1fr", "80px", "100px", "100px", "80px"]} />
        </SkeletonList>
      </Card>
    );
  }
  if (milestones.length === 0) {
    return <Card title={project.title}><Empty>No invoices yet</Empty></Card>;
  }

  const total = milestones.reduce((n, m) => n + (m.amount || 0), 0);
  const paid = milestones.filter((m) => m.status === "paid").reduce((n, m) => n + (m.amount || 0), 0);
  const outstanding = total - paid;

  return (
    <Card
      title={project.title}
      action={
        <span style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
          {fmt(paid)} paid · {fmt(outstanding)} outstanding
        </span>
      }
    >
      <div className="fw-invoice-table fw-invoice-table--client">
        <div className="fw-invoice-table__head">
          <span>Milestone</span>
          <span style={{ textAlign: "right" }}>Amount</span>
          <span>Status</span>
          <span>Paid</span>
          <span style={{ textAlign: "right" }}>Action</span>
        </div>
        {milestones.map((m) => (
          <div key={m.id} className="fw-invoice-row fw-invoice-row--client">
            <div>
              <div>{m.label}</div>
              <div style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
                #{String((m.order ?? 0) + 1).padStart(2, "0")} · {Math.round((m.pct || 0) * 100)}%
              </div>
            </div>
            <div style={{ textAlign: "right", fontFamily: "var(--fw-mono)" }}>{fmt(m.amount)}</div>
            <div><StageChip status={chipFor(m.status)} /></div>
            <div style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
              {m.status === "paid" ? fmtDate(m.paidAt) : "—"}
            </div>
            <div style={{ textAlign: "right" }}>
              {m.status !== "paid" ? (
                <button
                  className="fw-btn fw-btn--primary"
                  style={{ padding: "6px 12px" }}
                  onClick={() => setPayingId(m.id)}
                >
                  Pay
                </button>
              ) : (
                <span style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>—</span>
              )}
            </div>
          </div>
        ))}
      </div>
      {payingId && (
        <PayMilestoneModal
          milestone={milestones.find((m) => m.id === payingId)}
          project={project}
          onClose={() => setPayingId(null)}
        />
      )}
    </Card>
  );
};

const InvoicesSection = () => {
  const { user, profile } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [allMilestones, setAllMilestones] = useState({});
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState(null);

  const handlePortal = async () => {
    setPortalBusy(true);
    setPortalError(null);
    try {
      await openCustomerPortal({ user, profile });
    } catch (err) {
      setPortalError(err?.response?.data?.error || err.message || "Could not open billing portal");
      setPortalBusy(false);
    }
  };

  useEffect(() => {
    if (!profile?.accountId) {
      setProjectsLoaded(true);
      return;
    }
    return subscribeAccountProjects(profile.accountId, (v) => {
      setProjects(v);
      setProjectsLoaded(true);
    });
  }, [profile?.accountId]);

  const showSkeleton = useMinLoading(!projectsLoaded);

  useEffect(() => {
    const unsubs = projects.map((p) =>
      subscribeMilestones(p.id, (ms) =>
        setAllMilestones((prev) => ({ ...prev, [p.id]: ms }))
      )
    );
    return () => unsubs.forEach((u) => u && u());
  }, [projects]);

  const flat = Object.values(allMilestones).flat();
  const paid = flat.filter((m) => m.status === "paid").reduce((n, m) => n + (m.amount || 0), 0);
  const outstanding = flat
    .filter((m) => m.status !== "paid")
    .reduce((n, m) => n + (m.amount || 0), 0);

  if (showSkeleton) {
    return (
      <>
        <div className="fw-stat-grid">
          <Stat label="Paid" value={fmt(paid)} loading />
          <Stat label="Outstanding" value={fmt(outstanding)} loading />
          <Stat label="Projects" value={projects.length} loading />
        </div>
        <Card title="Invoices">
          <SkeletonList count={3} ariaLabel="Loading invoices">
            <SkeletonRow cols={5} widths={["1fr", "80px", "100px", "100px", "80px"]} />
          </SkeletonList>
        </Card>
      </>
    );
  }

  if (projects.length === 0) {
    return <Card title="Invoices"><Empty>No projects yet</Empty></Card>;
  }

  return (
    <>
      <div className="fw-stat-grid">
        <Stat label="Paid" value={fmt(paid)} />
        <Stat label="Outstanding" value={fmt(outstanding)} />
        <Stat label="Projects" value={projects.length} />
      </div>
      <Card
        title="Billing"
        action={
          <button className="fw-btn fw-btn--ghost" onClick={handlePortal} disabled={portalBusy}>
            {portalBusy ? "Opening…" : "Manage billing"}
          </button>
        }
      >
        <div style={{ fontSize: 13, color: "var(--fw-muted)" }}>
          Update your card on file, download past invoices + PDFs, or cancel a subscription via
          Stripe's secure customer portal.
        </div>
        {portalError && (
          <div style={{ color: "#c0392b", fontSize: 12, fontFamily: "var(--fw-mono)" }}>{portalError}</div>
        )}
      </Card>
      {projects.map((p) => (
        <ProjectInvoices key={p.id} project={p} />
      ))}
    </>
  );
};

export default InvoicesSection;
